import { useState } from 'react';
import type { FormEvent } from 'react';
import { OpsApiError } from '../api/client.ts';
import {
  useSoftDeleteAccount,
  useSuspendAccount,
  useUnsuspendAccount,
} from '../api/useAdmin.ts';
import { Modal } from './Modal.tsx';
import { StatusChip } from './StatusChip.tsx';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

/**
 * Account lifecycle card — suspend / unsuspend / soft-delete. Epic #109
 * P2.10.
 *
 * Shown on AccountDetail right after the Summary card so ops can see
 * the status chip at the top and take action without scrolling. The
 * card gates its actions on the current state:
 *   - active: Suspend + Delete.
 *   - suspended: Unsuspend + Delete (suspension reason displayed).
 *   - deleted: read-only; no further actions permitted from the UI.
 *
 * All three mutations are type-to-confirm to match the balance-adjust
 * and gift-card-revoke patterns — the email is the confirmation token
 * for suspend/delete (less typo-prone than the opaque account id).
 */

interface Props {
  readonly accountId: string;
  readonly email: string;
  readonly suspendedAt: string | null;
  readonly suspensionReason: string | null;
  readonly deletedAt: string | null;
}

type DraftAction = 'suspend' | 'unsuspend' | 'delete';

interface Draft {
  readonly action: DraftAction;
  readonly reason: string;
}

function statusVariant(
  suspendedAt: string | null,
  deletedAt: string | null,
): 'ok' | 'warning' | 'error' {
  if (deletedAt !== null) return 'error';
  if (suspendedAt !== null) return 'warning';
  return 'ok';
}

export function AccountLifecycleCard({
  accountId,
  email,
  suspendedAt,
  suspensionReason,
  deletedAt,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const suspend = useSuspendAccount();
  const unsuspend = useUnsuspendAccount();
  const softDelete = useSoftDeleteAccount();

  const [suspendReason, setSuspendReason] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [typed, setTyped] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);

  const busy = suspend.isPending || unsuspend.isPending || softDelete.isPending;
  const variant = statusVariant(suspendedAt, deletedAt);
  const statusLabel =
    deletedAt !== null
      ? t('account_lifecycle.status.deleted')
      : suspendedAt !== null
      ? t('account_lifecycle.status.suspended')
      : t('account_lifecycle.status.active');
  const canSuspend = suspendedAt === null && deletedAt === null;
  const canUnsuspend = suspendedAt !== null && deletedAt === null;
  const canDelete = deletedAt === null;
  const typedMatches = typed.trim() === email;

  function openConfirm(action: DraftAction): void {
    setModalError(null);
    setTyped('');
    setDraft({
      action,
      reason: action === 'suspend' ? suspendReason.trim() : '',
    });
  }

  function closeConfirm(): void {
    setDraft(null);
    setTyped('');
    setModalError(null);
  }

  async function handleConfirm(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setModalError(null);
    if (draft === null) return;
    if (!typedMatches) {
      setModalError(t('account_lifecycle.confirm.type_mismatch', { email }));
      return;
    }
    try {
      if (draft.action === 'suspend') {
        await suspend.mutateAsync({
          accountId,
          // Omit `reason` entirely when blank so the wire payload
          // matches `SuspendAccountRequest.reason?: string`. A prior
          // version sent `{ reason: '' }`, which the backend happened
          // to normalize to null but violated the declared contract.
          body: draft.reason.length > 0 ? { reason: draft.reason } : {},
        });
        toast.success(t('account_lifecycle.toast.suspended', { email }));
        setSuspendReason('');
      } else if (draft.action === 'unsuspend') {
        await unsuspend.mutateAsync({ accountId });
        toast.success(t('account_lifecycle.toast.unsuspended', { email }));
      } else {
        await softDelete.mutateAsync({ accountId });
        toast.success(t('account_lifecycle.toast.deleted', { email }));
      }
      closeConfirm();
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setModalError(message);
    }
  }

  return (
    <article
      data-testid="account-lifecycle-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('account_lifecycle.title')}
          </h3>
          <StatusChip
            variant={variant}
            label={statusLabel}
            testId="account-lifecycle-status"
          />
        </div>
      </header>

      {suspendedAt !== null && deletedAt === null ? (
        <p
          role="status"
          className="rounded-lg bg-tertiary-container px-4 py-3 text-xs text-on-tertiary-container"
          data-testid="account-lifecycle-suspension-banner"
        >
          {t('account_lifecycle.suspended.banner', {
            iso: suspendedAt,
            reason: suspensionReason ?? t('account_lifecycle.suspended.no_reason'),
          })}
        </p>
      ) : null}

      {deletedAt !== null ? (
        <p
          role="status"
          className="rounded-lg bg-error-container px-4 py-3 text-xs text-on-error-container"
          data-testid="account-lifecycle-deleted-banner"
        >
          {t('account_lifecycle.deleted.banner', { iso: deletedAt })}
        </p>
      ) : null}

      {canSuspend ? (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('account_lifecycle.suspend.reason_label')}
            </span>
            <input
              type="text"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.currentTarget.value)}
              placeholder={t('account_lifecycle.suspend.reason_placeholder')}
              data-testid="account-lifecycle-suspend-reason"
              className={INPUT_CLASS}
              disabled={busy}
            />
            <span className="text-[11px] text-outline">
              {t('account_lifecycle.suspend.reason_hint')}
            </span>
          </label>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        {canSuspend ? (
          <button
            type="button"
            onClick={() => openConfirm('suspend')}
            disabled={busy}
            data-testid="account-lifecycle-suspend"
            className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('account_lifecycle.suspend.button')}
          </button>
        ) : null}
        {canUnsuspend ? (
          <button
            type="button"
            onClick={() => openConfirm('unsuspend')}
            disabled={busy}
            data-testid="account-lifecycle-unsuspend"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('account_lifecycle.unsuspend.button')}
          </button>
        ) : null}
        {canDelete ? (
          <button
            type="button"
            onClick={() => openConfirm('delete')}
            disabled={busy}
            data-testid="account-lifecycle-delete"
            className="inline-flex items-center justify-center rounded-lg bg-error-container px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-error-container transition-colors hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('account_lifecycle.delete.button')}
          </button>
        ) : null}
      </div>

      {draft !== null ? (
        <Modal
          isOpen
          onClose={() => {
            if (!busy) closeConfirm();
          }}
          titleId="account-lifecycle-confirm-title"
          testId="account-lifecycle-modal"
        >
          <form
            onSubmit={handleConfirm}
            className="flex flex-col gap-6 p-8"
            noValidate
          >
            <h2
              id="account-lifecycle-confirm-title"
              className="text-base font-semibold text-on-surface"
            >
              {t(
                draft.action === 'suspend'
                  ? 'account_lifecycle.confirm.title_suspend'
                  : draft.action === 'unsuspend'
                  ? 'account_lifecycle.confirm.title_unsuspend'
                  : 'account_lifecycle.confirm.title_delete',
                { email },
              )}
            </h2>
            <p className="text-sm text-outline">
              {t(
                draft.action === 'suspend'
                  ? 'account_lifecycle.confirm.body_suspend'
                  : draft.action === 'unsuspend'
                  ? 'account_lifecycle.confirm.body_unsuspend'
                  : 'account_lifecycle.confirm.body_delete',
                { email },
              )}
            </p>
            {draft.action === 'suspend' && draft.reason.length > 0 ? (
              <p className="rounded-lg bg-surface-container-low px-3 py-2 text-xs text-on-surface-variant">
                {t('account_lifecycle.confirm.reason_preview', { reason: draft.reason })}
              </p>
            ) : null}
            <label className="flex flex-col gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('account_lifecycle.confirm.type_email', { email })}
              </span>
              <input
                type="text"
                value={typed}
                onChange={(e) => setTyped(e.currentTarget.value)}
                placeholder={email}
                data-testid="account-lifecycle-modal-typed"
                className={INPUT_CLASS}
                autoComplete="off"
                disabled={busy}
              />
            </label>
            {modalError !== null ? (
              <p
                role="alert"
                className="text-xs font-medium text-error"
                data-testid="account-lifecycle-modal-error"
              >
                {modalError}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeConfirm}
                disabled={busy}
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={busy || !typedMatches}
                data-testid="account-lifecycle-modal-submit"
                className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
              >
                {busy
                  ? t('common.saving')
                  : t(
                      draft.action === 'suspend'
                        ? 'account_lifecycle.confirm.submit_suspend'
                        : draft.action === 'unsuspend'
                        ? 'account_lifecycle.confirm.submit_unsuspend'
                        : 'account_lifecycle.confirm.submit_delete',
                    )}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </article>
  );
}
